import React from 'react';
import { FiX, FiDownload, FiExternalLink } from 'react-icons/fi';
import { WalletTransaction } from '@/types/wallet';
import { formatCurrency, formatDate } from '@/utils/formatters';
import TransactionTypeIcon from './TransactionTypeIcon';
import TransactionStatusBadge from './TransactionStatusBadge';

interface TransactionDetailsModalProps {
  transaction: WalletTransaction | null;
  isOpen: boolean;
  onClose: () => void;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  transaction,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !transaction) return null;

  const getTransactionLabel = (type: string): string => {
    const labels: Record<string, string> = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer_in: 'Transfer Received',
      transfer_out: 'Transfer Sent',
      escrow_release: 'Escrow Released',
      escrow_hold: 'Escrow Hold',
      escrow_refund: 'Escrow Refund',
      commission_deduction: 'Commission',
      refund: 'Refund',
      bonus: 'Bonus',
      penalty: 'Penalty',
      adjustment: 'Adjustment',
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-gray-800 sm:p-5">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
            Transaction Details
          </h2>
          <button
            onClick={onClose}
            title="Close transaction details"
            className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <FiX size={24} />
          </button>
        </div>

        <div className="space-y-5 p-4 sm:p-5">
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-slate-100 p-3 dark:bg-gray-800">
              <TransactionTypeIcon type={transaction.type} size={32} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
                  {getTransactionLabel(transaction.type)}
                </h3>
                <TransactionStatusBadge status={transaction.status} />
              </div>
              <p className="text-slate-600 dark:text-gray-400">{transaction.description}</p>
            </div>
            <div className="text-right">
              <p
                className={`text-xl font-bold sm:text-2xl ${
                  transaction.direction === 'credit'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {transaction.direction === 'credit' ? '+' : '-'}
                {formatCurrency(transaction.amount, transaction.currency)}
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                {formatDate(String(transaction.createdAt))}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Transaction ID</p>
              <p className="font-medium text-slate-900 dark:text-white">
                {transaction.transactionId}
              </p>
            </div>

            {transaction.referenceNumber && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Reference</p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {transaction.referenceNumber}
                </p>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Amount</p>
              <p className="font-medium text-slate-900 dark:text-white">
                {formatCurrency(transaction.amount, transaction.currency)}
              </p>
            </div>

            {transaction.fee > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Fee</p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {formatCurrency(transaction.fee, transaction.currency)}
                </p>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Net Amount</p>
              <p className="font-medium text-slate-900 dark:text-white">
                {formatCurrency(transaction.netAmount, transaction.currency)}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Currency</p>
              <p className="font-medium text-slate-900 dark:text-white">
                {transaction.currency}
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-5 dark:border-gray-800">
            <h4 className="mb-4 font-semibold text-slate-900 dark:text-white">
              Balance Changes
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Balance Before</p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {formatCurrency(transaction.balanceBefore, transaction.currency)}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Balance After</p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {formatCurrency(transaction.balanceAfter, transaction.currency)}
                </p>
              </div>
            </div>
          </div>

          {transaction.paymentMethod && (
            <div className="border-t border-slate-200 pt-5 dark:border-gray-800">
              <h4 className="mb-4 font-semibold text-slate-900 dark:text-white">
                Payment Method
              </h4>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-slate-600 dark:text-gray-400">Type:</span>
                  <span className="font-medium text-slate-900 dark:text-white capitalize">
                    {transaction.paymentMethod.type.replace(/_/g, ' ')}
                  </span>
                </div>
                {transaction.paymentMethod.provider && (
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600 dark:text-gray-400">Provider:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {transaction.paymentMethod.provider}
                    </span>
                  </div>
                )}
                {transaction.paymentMethod.accountInfo && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600 dark:text-gray-400">Account:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {transaction.paymentMethod.accountInfo}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {transaction.relatedEntity && (
            <div className="border-t border-slate-200 pt-5 dark:border-gray-800">
              <h4 className="mb-4 font-semibold text-slate-900 dark:text-white">
                Related Information
              </h4>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-slate-600 dark:text-gray-400">Type:</span>
                  <span className="font-medium text-slate-900 dark:text-white capitalize">
                    {transaction.relatedEntity.type}
                  </span>
                </div>
                {transaction.metadata?.gigTitle && (
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600 dark:text-gray-400">Gig:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {transaction.metadata.gigTitle}
                    </span>
                  </div>
                )}
                {transaction.relatedEntity.reference && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600 dark:text-gray-400">Reference:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {transaction.relatedEntity.reference}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {transaction.status === 'failed' && transaction.failureReason && (
            <div className="border-t border-slate-200 pt-5 dark:border-gray-800">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                  Failure Reason
                </h4>
                <p className="text-sm text-red-800 dark:text-red-200">
                  {transaction.failureReason}
                </p>
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 pt-5 dark:border-gray-800">
            <h4 className="mb-4 font-semibold text-slate-900 dark:text-white">Timeline</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-600 mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Initiated
                  </p>
                  <p className="text-xs text-slate-600 dark:text-gray-400">
                    {formatDate(String(transaction.initiatedAt))}
                  </p>
                </div>
              </div>

              {transaction.processedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary-600 mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      Processed
                    </p>
                    <p className="text-xs text-slate-600 dark:text-gray-400">
                      {formatDate(String(transaction.processedAt))}
                    </p>
                  </div>
                </div>
              )}

              {transaction.completedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-600 mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      Completed
                    </p>
                    <p className="text-xs text-slate-600 dark:text-gray-400">
                      {formatDate(String(transaction.completedAt))}
                    </p>
                  </div>
                </div>
              )}

              {transaction.failedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-600 mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      Failed
                    </p>
                    <p className="text-xs text-slate-600 dark:text-gray-400">
                      {formatDate(String(transaction.failedAt))}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 border-t border-slate-200 p-4 dark:border-gray-800 sm:p-5">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
